"use client"

import { useState, useEffect, useCallback, useMemo, memo } from "react"
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  Filter,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  Upload,
  File,
  FileText
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { useToast } from "../../../components/ui/use-toast"
import {
  getAllApprovalRules,
  createApprovalRule,
  updateApprovalRule,
  deleteApprovalRule,
  generateBusinessRuleWithAI,
  APPROVAL_RULE_OPERATORS,
  createRuleTemplate
} from "../../../../ApiCalls/mantisFlowsApprovalRules"

// Agregar estilos CSS personalizados
const customStyles = `
  .priority-indicator {
    position: relative;
  }
  
  .priority-indicator::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: inherit;
    opacity: 0.2;
    z-index: -1;
  }
`

// Componente memoizado para el textarea de IA
const AIPromptTextarea = memo(({ value, onChange, disabled, placeholder, className }) => {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
    />
  )
})

AIPromptTextarea.displayName = 'AIPromptTextarea'

function ApprovalRulesModal({ clientId, onClose, embedded = false }) {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingRule, setEditingRule] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState(() => createRuleTemplate())
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [rulePrompt, setRulePrompt] = useState("")
  const [generatingRule, setGeneratingRule] = useState(false)
  const [generatedRule, setGeneratedRule] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [processingFiles, setProcessingFiles] = useState(false)
  const [deletingRule, setDeletingRule] = useState(false)
  
  const { toast } = useToast()

  // Memoized handlers
  const handleRulePromptChange = useCallback((e) => {
    setRulePrompt(e.target.value)
  }, [])

  const handleCreateRule = useCallback(() => {
    setFormData(createRuleTemplate())
    setEditingRule(null)
    setRulePrompt("")
    setGeneratedRule(null)
    setUploadedFiles([])
    setShowForm(true)
  }, [])

  const loadRules = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getAllApprovalRules(clientId)
      
      // Debug: Log para ver qué devuelve la API (remover en producción)
      console.log("API Response for approval rules:", data)
      
      // La función getAllApprovalRules ya maneja la validación y siempre devuelve un array
      setRules(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Error loading approval rules:", error)
      setRules([]) // Asegurar que rules sea siempre un array
      toast({
        title: "Error",
        description: "Error al cargar las reglas de aprobación. Por favor intenta de nuevo.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }, [clientId, toast])

  // Load rules on component mount
  useEffect(() => {
    loadRules()
  }, [loadRules])

  // Función para convertir archivo a base64
  const convertFileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64 = reader.result.split(',')[1] // Remover el prefijo data:...;base64,
        resolve(base64)
      }
      reader.onerror = error => reject(error)
    })
  }, [])

  // Manejar la carga de archivos
  const handleFileUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files)
    
    if (files.length === 0) return
    
    try {
      setProcessingFiles(true)
      
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          // Validar tipo de archivo
          const allowedTypes = ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
          if (!allowedTypes.includes(file.type)) {
            throw new Error(`Tipo de archivo no soportado: ${file.type}. Solo se permiten PDF, TXT y DOC/DOCX.`)
          }
          
          // Validar tamaño (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            throw new Error(`El archivo ${file.name} es demasiado grande. Máximo 10MB.`)
          }
          
          const base64 = await convertFileToBase64(file)
          
          return {
            name: file.name,
            base64: base64,
            type: file.type,
            size: file.size
          }
        })
      )
      
      setUploadedFiles(prev => [...prev, ...processedFiles])
      
      toast({
        title: "Archivos cargados",
        description: `${processedFiles.length} archivo(s) cargado(s) exitosamente`
      })
    } catch (error) {
      console.error("Error processing files:", error)
      toast({
        title: "Error",
        description: error.message || "Error al procesar los archivos",
        variant: "destructive"
      })
    } finally {
      setProcessingFiles(false)
      // Limpiar el input
      event.target.value = ''
    }
  }, [convertFileToBase64, toast])

  // Remover archivo cargado
  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleEditRule = useCallback((rule) => {
    // Adaptar la regla al formato del formulario
    let adaptedRule = { ...rule }
    
    // Si es una regla generada por IA, preparar los datos de manera especial
    if (rule.conditions?.created_by_ai) {
      adaptedRule = {
        ...rule,
        conditions: [], // Las reglas de IA no usan el formato traditional de condiciones
        isAIGenerated: true,
        aiQuery: rule.conditions.query,
        aiDescription: rule.conditions.technical_description,
        aiSource: rule.conditions.source
      }
    }
    
    setFormData(adaptedRule)
    setEditingRule(rule.id)
    setRulePrompt("")
    setGeneratedRule(null)
    setUploadedFiles([])
    setShowForm(true)
  }, [])

  const generateRule = useCallback(async () => {
    if (!rulePrompt.trim() && uploadedFiles.length === 0) {
      toast({
        title: "Error",
        description: "Por favor proporciona una descripción o sube documentos para generar la regla",
        variant: "destructive"
      })
      return
    }

    try {
      setGeneratingRule(true)
      
      const options = {}
      
      if (rulePrompt.trim()) {
        options.prompt = rulePrompt.trim()
      }
      
      if (uploadedFiles.length > 0) {
        options.documents = uploadedFiles
      }
      
      const aiResult = await generateBusinessRuleWithAI(clientId, options)
      console.log("AI Result:", aiResult)
      if (aiResult && aiResult.success) {
        /* const newRule = {
          ...createRuleTemplate(),
          name: `${aiResult.description || rulePrompt || 'Reglas desde documento'}`,
          conditions: {
            created_by_ai: true,
            query: rulePrompt,
            source: uploadedFiles.length > 0 ? 'document' : 'prompt',
            documents: uploadedFiles.length > 0 ? uploadedFiles.map(f => f.name) : [],
            technical_description: aiResult.technical_description || 'Regla generada por IA',
            business_logic: aiResult.business_logic || [],
            ai_rules_data: aiResult
          },
          enabled: true,
          priority: 1,
          required_approvals: ["gerente_general"] // Default approval
        } */

        setGeneratedRule(aiResult);
        //setFormData(newRule)

        // Crear automáticamente la regla
        //await createApprovalRule(clientId, newRule)

        toast({
          title: "¡Regla creada exitosamente!",
          description: "Tu regla de aprobación ha sido generada y guardada"
        });

        // Regresar a la lista y refrescar
        await loadRules();
        setShowForm(false);
        setEditingRule(null);
        setFormData(createRuleTemplate());
        setRulePrompt("");
        setGeneratedRule(null);
        setUploadedFiles([]);
      } else {
        throw new Error(aiResult?.message || "No se pudo generar la regla")
      }
    } catch (error) {
      console.error("Error generating AI rule:", error)
      toast({
        title: "Error",
        description: "Error al generar la regla. Por favor intenta de nuevo.",
        variant: "destructive"
      })
    } finally {
      setGeneratingRule(false)
    }
  }, [rulePrompt, uploadedFiles, clientId, toast, loadRules])

  const saveRule = useCallback(async () => {
    try {
      setSaving(true)
      
      if (editingRule) {
        await updateApprovalRule(clientId, editingRule, formData)
        toast({
          title: "Regla actualizada",
          description: "La regla de aprobación ha sido actualizada correctamente"
        })
      } else {
        await createApprovalRule(clientId, formData)
        toast({
          title: "Regla creada",
          description: "La regla de aprobación ha sido creada correctamente"
        })
      }
      
      await loadRules()
      setShowForm(false)
      setEditingRule(null)
      setFormData(createRuleTemplate())
      setRulePrompt("")
      setGeneratedRule(null)
      setUploadedFiles([])
    } catch (error) {
      console.error("Error saving rule:", error)
      toast({
        title: "Error",
        description: "Error al guardar la regla. Por favor intenta de nuevo.",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }, [editingRule, clientId, formData, loadRules, toast])

  const deleteRule = useCallback(async (ruleId) => {
    try {
      setDeletingRule(true)
      await deleteApprovalRule(clientId, ruleId)
      await loadRules()
      setDeleteConfirm(null)
      toast({
        title: "Regla eliminada",
        description: "La regla de aprobación ha sido eliminada correctamente"
      })
    } catch (error) {
      console.error("Error deleting rule:", error)
      toast({
        title: "Error", 
        description: "Error al eliminar la regla. Por favor intenta de nuevo.",
        variant: "destructive"
      })
    } finally {
      setDeletingRule(false)
    }
  }, [clientId, loadRules, toast])

  // Memoized values
  const isFormDisabled = useMemo(() => generatingRule || processingFiles, [generatingRule, processingFiles])
  const canGenerateRule = useMemo(() => rulePrompt.trim() || uploadedFiles.length > 0, [rulePrompt, uploadedFiles.length])

  const getPriorityColor = useCallback((priority) => {
    if (priority === 1) return "bg-red-500 shadow-red-200"
    if (priority <= 3) return "bg-orange-500 shadow-orange-200"
    if (priority <= 5) return "bg-yellow-500 shadow-yellow-200"
    return "bg-green-500 shadow-green-200"
  }, [])

  const getOperatorLabel = useCallback((operator) => {
    const labels = {
      equals: "Equals",
      not_equals: "Not Equals",
      greater_than: "Greater Than",
      less_than: "Less Than",
      greater_equal: "Greater or Equal",
      less_equal: "Less or Equal",
      contains: "Contains",
      not_contains: "Does Not Contain",
      starts_with: "Starts With",
      ends_with: "Ends With",
      in: "In List",
      not_in: "Not In List"
    }
    return labels[operator] || operator
  }, [])

  const getApprovalDisplayName = useCallback((approval) => {
    const displayNames = {
      comite_compras: "Comité de Compras",
      gerente_general: "Gerente General",
      jefe_financiero: "Jefe Financiero",
      director_operaciones: "Director de Operaciones",
      supervisor_compras: "Supervisor de Compras"
    }
    return displayNames[approval] || approval.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }, [])

  return (
    <>
      {/* Inyectar estilos personalizados */}
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Reglas de Aprobación
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {rules.length} reglas configuradas
              </p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
            <span className="text-sm text-slate-500 dark:text-slate-400">Cargando reglas...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Main Content */}
          {!showForm ? (
            <div className="p-6">
              {/* Action Bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Filtros
                  </span>
                </div>
                <Button
                  onClick={handleCreateRule}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Regla
                </Button>
              </div>

              {/* Rules List */}
              {rules.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                    No hay reglas configuradas
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                    Crea tu primera regla de aprobación para automatizar el flujo de trabajo
                  </p>
                  <Button
                    onClick={handleCreateRule}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primera Regla
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {rules.map((rule) => (
                    <div
                      key={rule.id}
                      className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div
                              className={`w-3 h-3 rounded-full ${getPriorityColor(rule.priority)} priority-indicator`}
                            />
                            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                              {rule.name}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              rule 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {rule ? 'Activa' : 'Inactiva'}
                            </span>
                          </div>

                          {/* Rule Details - Only Required Approvals */}
                          <div className="space-y-2">
                            {/* Required Approvals */}
                            {rule.required_approvals && rule.required_approvals.length > 0 && (
                              <div className="text-sm text-slate-600 dark:text-slate-400">
                                <span className="font-medium">Aprobaciones requeridas:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {rule.required_approvals.map((approval, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs"
                                    >
                                      {getApprovalDisplayName(approval)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditRule(rule)}
                            className="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(rule.id)}
                            className="text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Simplified Form - Only AI Generation */
            <div className="p-8">
              <div className="max-w-2xl mx-auto">
                {/* Form Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center mx-auto mb-4">
                    <div className="text-2xl">📋</div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                    Nueva Regla de Aprobación
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                    Describe tu regla de negocio en lenguaje natural o sube documentos con tus políticas
                  </p>
                </div>

                {/* Generation Section */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-6 border border-blue-200 dark:border-blue-700">
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
                      Describe tu regla de aprobación
                    </label>
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded p-3 mb-4 text-sm text-blue-800 dark:text-blue-200">
                      <strong>Ejemplos:</strong>
                      <ul className="mt-2 space-y-1 list-disc list-inside">
                        <li>"Solicitudes mayores a $5000 requieren aprobación del gerente"</li>
                        <li>"Compras del departamento de IT requieren aprobación del gerente de IT"</li>
                        <li>"Todas las órdenes de equipos médicos deben ser aprobadas por el director médico"</li>
                      </ul>
                    </div>
                    <AIPromptTextarea
                      value={rulePrompt}
                      onChange={handleRulePromptChange}
                      placeholder="Escribe aquí tu regla de negocio en lenguaje natural..."
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-200 min-h-[120px] resize-none"
                      disabled={isFormDisabled}
                    />
                  </div>

                  {/* Separador O */}
                  <div className="flex items-center my-6">
                    <div className="flex-1 border-t border-slate-300 dark:border-slate-600"></div>
                    <span className="px-4 text-sm font-medium text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 rounded-full">O</span>
                    <div className="flex-1 border-t border-slate-300 dark:border-slate-600"></div>
                  </div>

                  {/* Sección de carga de documentos */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
                      Cargar documentos con políticas
                    </label>
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded p-3 mb-4 text-sm text-blue-800 dark:text-blue-200">
                      Sube documentos PDF, TXT o DOC que contengan tus políticas de aprobación
                    </div>
                    
                    {/* Input de archivos */}
                    <div className="border-2 border-dashed border-blue-300 dark:border-blue-600 rounded p-6 text-center hover:border-blue-400 transition-colors bg-white dark:bg-slate-800">
                      <input
                        type="file"
                        id="file-upload"
                        multiple
                        accept=".pdf,.txt,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={generatingRule || processingFiles}
                      />
                      <label 
                        htmlFor="file-upload" 
                        className={`cursor-pointer flex flex-col items-center ${generatingRule || processingFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {processingFiles ? (
                          <>
                            <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                            <span className="text-sm text-slate-600 dark:text-slate-400">Procesando archivos...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-10 w-10 text-blue-400 mb-3" />
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Haz clic para cargar archivos o arrastra y suelta
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                              PDF, TXT, DOC, DOCX (Máx. 10MB)
                            </span>
                          </>
                        )}
                      </label>
                    </div>

                    {/* Lista de archivos cargados */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Archivos cargados:
                        </h5>
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
                            <div className="flex items-center space-x-3">
                              <FileText className="h-5 w-5 text-blue-500" />
                              <div>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{file.name}</span>
                                <div className="text-xs text-slate-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Botón para generar regla */}
                  <div className="flex justify-center">
                    <Button
                      onClick={generateRule}
                      disabled={generatingRule || processingFiles || !canGenerateRule}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-3 text-lg font-medium rounded shadow-lg"
                      size="lg"
                    >
                      {generatingRule ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                          Generando regla...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Crear Regla
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="text-slate-600 border-slate-300 hover:bg-slate-50"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                  Eliminar Regla
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              ¿Estás seguro de que quieres eliminar esta regla de aprobación? Esto podría afectar los flujos de trabajo actuales.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => deleteRule(deleteConfirm)}
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={deletingRule}
              >
                {deletingRule ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                    Eliminando regla...
                  </>
                ) : (
                  "Eliminar Regla"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default memo(ApprovalRulesModal) 